import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import type { ProductWithDetails, Laboratory } from '../types';

const STORAGE_KEYS = {
  PRODUCTS: 'products',
  LABORATORIES: 'laboratories',
  LAST_SYNC: 'lastSync',
} as const;

export const useOfflineStore = defineStore('offline', () => {
  const lastSync = ref<Date | null>(null);
  const isOnline = ref(navigator.onLine);

  // Load initial data from localStorage
  const loadFromStorage = () => {
    try {
      const storedProducts = localStorage.getItem(STORAGE_KEYS.PRODUCTS);
      const storedLabs = localStorage.getItem(STORAGE_KEYS.LABORATORIES);
      const storedLastSync = localStorage.getItem(STORAGE_KEYS.LAST_SYNC);

      return {
        products: storedProducts ? JSON.parse(storedProducts) : [],
        laboratories: storedLabs ? JSON.parse(storedLabs) : [],
        lastSync: storedLastSync ? new Date(storedLastSync) : null,
      };
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      return { products: [], laboratories: [], lastSync: null };
    }
  };

  // Save data to localStorage
  const saveToStorage = (data: {
    products?: ProductWithDetails[],
    laboratories?: Laboratory[],
  }) => {
    try {
      if (data.products) {
        localStorage.setItem(STORAGE_KEYS.PRODUCTS, JSON.stringify(data.products));
      }
      if (data.laboratories) {
        localStorage.setItem(STORAGE_KEYS.LABORATORIES, JSON.stringify(data.laboratories));
      }
      const now = new Date();
      localStorage.setItem(STORAGE_KEYS.LAST_SYNC, now.toISOString());
      lastSync.value = now;
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  // Clear storage
  const clearStorage = () => {
    try {
      localStorage.removeItem(STORAGE_KEYS.PRODUCTS);
      localStorage.removeItem(STORAGE_KEYS.LABORATORIES);
      localStorage.removeItem(STORAGE_KEYS.LAST_SYNC);
      lastSync.value = null;
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  };

  // Online/Offline detection
  window.addEventListener('online', () => isOnline.value = true);
  window.addEventListener('offline', () => isOnline.value = false);

  return {
    lastSync,
    isOnline,
    loadFromStorage,
    saveToStorage,
    clearStorage,
  };
});